// Clients, the work (retainers, jobs, publications, documents) and agreements — part of the API surface; views import from '../api' (index.js), never from here.
import { get, post, put, del, postForm } from './http';

export const clientsApi = {

  // scope: 'customers' (default, won only — billing pickers) | 'nonleads' (the clients
  // screen: everyone who is no longer a prospect) | 'leads' | 'all'. `mine` scopes
  // leads to ones I own or have an open task on.
  list: (scope = 'customers', mine = false) => {
    const q = new URLSearchParams({ scope });
    if (mine) q.set('mine', '1');
    return get(`/clients?${q.toString()}`);
  },
  get: (id) => get(`/clients/${id}`),
  create: (body) => post('/clients', body),
  update: (id, body) => put(`/clients/${id}`, body),
  // A client's contact people, replaced whole (migration 071) — the card's contacts dialog.
  setContacts: (id, contacts) => put(`/clients/${id}/contacts`, { contacts }),
  advanceStage: (id, stage) => post(`/clients/${id}/stage`, { stage }),
  // Show or hide the client on the clients list. Nothing else changes — no figure in
  // the system reads this flag, and nothing is destroyed either way.
  setActive: (id, isActive) => post(`/clients/${id}/active`, { is_active: isActive }),
  // What this client has cost us — absorbed, charged on and never stated, per job
  // (migration 054). Reporting only: no figure in the system is computed from it.
  costs: (id) => get(`/clients/${id}/costs`),
  // What a delete would destroy: the client's retainers and standalone jobs by name,
  // plus the totals from everything beneath them.
  deletionPreview: (id) => get(`/clients/${id}/deletion-preview`),
  // Permanent, and takes every retainer and job of the client with it — their invoices,
  // payments, expenses, tasks, publications and stored files included.
  remove: (id) => del(`/clients/${id}`),

  // The leads screen's bulk delete: one lead, several, or all of them. Both calls take
  // the SAME selection in the same form, so the confirmation is computed from exactly
  // the ids the delete then acts on. The server refuses the whole request if any id is
  // no longer a lead — a converted customer cannot be deleted through here.
  leadsDeletionPreview: (ids) => get(`/clients/leads/deletion-preview?ids=${ids.join(',')}`),
  removeLeads: (ids) => del(`/clients/leads?ids=${ids.join(',')}`),
};

export const publicationsApi = {
  // Coverage for one job (the job screen) or one client (the client card, flat
  // across all their jobs). Each publication comes back with its files attached.
  byJob: (jobId) => get(`/publications?job_id=${jobId}`),
  byClient: (clientId) => get(`/publications?client_id=${clientId}`),
  create: (body) => post('/publications', body),
  update: (id, body) => put(`/publications/${id}`, body),
  remove: (id) => del(`/publications/${id}`),
  // `outletCode` makes this a clipping for that outlet; omitted, the file belongs
  // to the publication itself (the Word document of the piece).
  addFile: (id, file, outletCode) => {
    const form = new FormData();
    form.append('file', file);
    if (outletCode) form.append('outlet_code', outletCode);
    return postForm(`/publications/${id}/files`, form);
  },
  removeFile: (fileId) => del(`/publications/files/${fileId}`),
  // Returns { url, fileName, inline } — a short-lived signed URL, not the file
  // itself. `inline` asks for a URL the browser will RENDER; the server grants it
  // only for types it has cleared, and the response says which it gave.
  downloadUrl: (fileId, { inline = false } = {}) =>
    get(`/publications/files/${fileId}/download-url${inline ? '?inline=1' : ''}`),
};

// The office filing cabinet: business documents that belong to no client — signed
// agreements, accounting paperwork. One document is one file, so the upload IS the
// create; there is no document without a file.
export const documentsApi = {
  // The live list and the archive are separate views, never mixed.
  list: ({ archived = false, category = null } = {}) => {
    const q = new URLSearchParams();
    if (archived) q.set('archived', '1');
    if (category) q.set('category', category);
    const qs = q.toString();
    return get(`/documents${qs ? `?${qs}` : ''}`);
  },
  create: (file, body) => {
    const form = new FormData();
    form.append('file', file);
    for (const [key, value] of Object.entries(body || {})) {
      if (value !== undefined && value !== null && value !== '') form.append(key, value);
    }
    return postForm('/documents', form);
  },
  // Metadata only — an edit never replaces the file.
  update: (id, body) => put(`/documents/${id}`, body),
  // Out of view, file untouched, reversible. The everyday "remove".
  setArchived: (id, archived) => put(`/documents/${id}/archived`, { archived }),
  // Permanent — the row and the stored file. Only ever called from the confirmation
  // dialog that names the document.
  remove: (id) => del(`/documents/${id}`),
  // Same contract as publications: a short-lived signed URL, not the file itself.
  downloadUrl: (id, { inline = false } = {}) =>
    get(`/documents/${id}/download-url${inline ? '?inline=1' : ''}`),
};

export const leadImportApi = {
  preview: (rows) => post('/lead-import/preview', { rows }),
  commit: (rows, exclude) => post('/lead-import/commit', { rows, exclude }),
};

// The income layer: an ongoing engagement billed as a whole, with the jobs beneath
// it holding the work. Its invoices, issuer and profit split live here.
export const retainersApi = {
  // `billing` narrows to one kind: 'monthly' (the retainers screen) or 'fixed' (the one-off
  // projects screen, migration 067). Omitted, every engagement — the job form's picker.
  list: ({ billing } = {}) => get(`/retainers${billing ? `?billing=${billing}` : ''}`),
  byClient: (clientId) => get(`/retainers?client_id=${clientId}`),
  get: (id) => get(`/retainers/${id}`),
  create: (body) => post('/retainers', body),
  update: (id, body) => put(`/retainers/${id}`, body),
  // Ending an engagement ('completed' / 'cancelled') or reopening one ('active').
  //
  // Its own call, not a field on `update`, because it is its own act: it writes the one
  // column and cannot reach the term, the monthly amount or the issuer. It removes the
  // expectation of FUTURE billing — the alerts and the forecast stop looking — and moves
  // no money: invoices already recorded and payments already collected are untouched, so
  // the settlement, the dashboard and the profit history read exactly as before.
  setStatus: (id, status) => put(`/retainers/${id}/status`, { status }),
  // What a delete would destroy: the retainer's own totals plus every job beneath it,
  // listed by name.
  deletionPreview: (id) => get(`/retainers/${id}/deletion-preview`),
  // Permanent, and takes every job beneath the retainer with it — their invoices,
  // payments, expenses, tasks, publications and stored files included, plus the
  // retainer's own signed agreements.
  remove: (id) => del(`/retainers/${id}`),

  // The SIGNED AGREEMENT: the contract behind the engagement. One agreement is one
  // file, so the upload IS the create — there is no agreement without a file. A
  // retainer holds many over its life (the original, then each renewal), and the live
  // list and the archive are separate views, never mixed.
  agreements: (retainerId, { archived = false } = {}) =>
    get(`/retainers/${retainerId}/agreements${archived ? '?archived=1' : ''}`),
  // Every agreement across ALL of one client's retainers — the client card's read.
  // Each row carries its retainer's title, so the trail back to the engagement is one
  // click away.
  clientAgreements: (clientId, { archived = false } = {}) => {
    const q = new URLSearchParams({ client_id: clientId });
    if (archived) q.set('archived', '1');
    return get(`/retainers/agreements?${q.toString()}`);
  },
  addAgreement: (retainerId, file, body) => {
    const form = new FormData();
    form.append('file', file);
    for (const [key, value] of Object.entries(body || {})) {
      if (value !== undefined && value !== null && value !== '') form.append(key, value);
    }
    return postForm(`/retainers/${retainerId}/agreements`, form);
  },
  // Metadata only — an edit never replaces the file, and never moves the agreement to
  // another retainer (the object key names the one it was uploaded under).
  updateAgreement: (id, body) => put(`/retainers/agreements/${id}`, body),
  // Out of view, file untouched, reversible. The ordinary act for a contract a
  // renewal has superseded.
  setAgreementArchived: (id, archived) =>
    put(`/retainers/agreements/${id}/archived`, { archived }),
  // Permanent — the row and the stored file. Only ever called from the confirmation
  // dialog that names the agreement.
  removeAgreement: (id) => del(`/retainers/agreements/${id}`),
  // Same contract as publications and documents: a short-lived signed URL, not the
  // file itself.
  agreementUrl: (id, { inline = false } = {}) =>
    get(`/retainers/agreements/${id}/download-url${inline ? '?inline=1' : ''}`),
};

export const jobsApi = {
  // `{ standalone: true }` asks for one-off projects only — jobs with no retainer above
  // them. Without it the list is every job including retainer topics, which is what the
  // client card's work tree and the task modal's job picker both need.
  list: ({ standalone = false } = {}) => get(standalone ? '/jobs?standalone=1' : '/jobs'),
  get: (id) => get(`/jobs/${id}`),
  create: (body) => post('/jobs', body),
  update: (id, body) => put(`/jobs/${id}`, body),
  // Finishing the work ('completed' / 'cancelled') or reopening it ('open').
  //
  // Its own call, not a field on `update`, because it is its own act: it writes the one
  // column and cannot reach the retainer, the income source, the issuer or the agreed
  // value. It moves no money — no calculation in the system reads a job's status, so
  // the expenses, invoices and payments on a finished job stay in the profit figures,
  // the partner balance and the dashboard exactly as they were.
  setStatus: (id, status) => put(`/jobs/${id}/status`, { status }),
  // What a delete would destroy — counts and money, read before it happens so the
  // confirmation can name each thing instead of warning in the abstract.
  deletionPreview: (id) => get(`/jobs/${id}/deletion-preview`),
  // Permanent, and takes the job's invoices, payments, expenses, tasks, publications,
  // signed agreements and all of their stored files with it.
  remove: (id) => del(`/jobs/${id}`),

  // The job's SIGNED AGREEMENT (migration 046) — typically the supplier contract behind
  // the work, the photographer's rather than the client's. Same shape as the retainer's
  // above, one level down, so one set of components renders both.
  agreements: (jobId, { archived = false } = {}) =>
    get(`/jobs/${jobId}/agreements${archived ? '?archived=1' : ''}`),
  clientAgreements: (clientId, { archived = false } = {}) => {
    const q = new URLSearchParams({ client_id: clientId });
    if (archived) q.set('archived', '1');
    return get(`/jobs/agreements?${q.toString()}`);
  },
  addAgreement: (jobId, file, body) => {
    const form = new FormData();
    form.append('file', file);
    for (const [key, value] of Object.entries(body || {})) {
      if (value !== undefined && value !== null && value !== '') form.append(key, value);
    }
    return postForm(`/jobs/${jobId}/agreements`, form);
  },
  updateAgreement: (id, body) => put(`/jobs/agreements/${id}`, body),
  setAgreementArchived: (id, archived) => put(`/jobs/agreements/${id}/archived`, { archived }),
  removeAgreement: (id) => del(`/jobs/agreements/${id}`),
  agreementUrl: (id, { inline = false } = {}) =>
    get(`/jobs/agreements/${id}/download-url${inline ? '?inline=1' : ''}`),

  // --- WORK FILES (migration 064): what a content writer sent in --------------------
  //
  // ⚠ THESE LIVE ON `/jobs`, WHICH IS STAFF, AND NOT ON `/uploads`. The writer uploads
  // through their own mount, which has no download route at all — a presigned URL needs
  // no further auth once minted, so the protection is that they have nothing to call.
  // Everything below is the MANAGER side.
  workFiles: (jobId) => get(`/jobs/${jobId}/work-files`),
  clientWorkFiles: (clientId) => get(`/jobs/work-files?client_id=${clientId}`),
  workFileUrl: (id, { inline = false } = {}) =>
    get(`/jobs/work-files/${id}/download-url${inline ? '?inline=1' : ''}`),
  // Opening one clears the doorbell that put it in the bell.
  markWorkFileSeen: (id) => put(`/jobs/work-files/${id}/seen`, {}),
  removeWorkFile: (id) => del(`/jobs/work-files/${id}`),
};

// The two agreement owners behind ONE interface, so the four agreement components take a
// single `api` prop instead of importing a specific resource module and branching on the
// owner. A retainer's contract and a job's differ in who signed them and in nothing about
// how they are listed, uploaded, archived or downloaded — which is exactly the kind of
// difference that belongs in a lookup rather than in the components.
//
// Adding a third owner means adding a key here and nothing else.
export const agreementApis = {
  retainer: {
    list: retainersApi.agreements,
    listByClient: retainersApi.clientAgreements,
    add: retainersApi.addAgreement,
    update: retainersApi.updateAgreement,
    setArchived: retainersApi.setAgreementArchived,
    remove: retainersApi.removeAgreement,
    url: retainersApi.agreementUrl,
  },
  job: {
    list: jobsApi.agreements,
    listByClient: jobsApi.clientAgreements,
    add: jobsApi.addAgreement,
    update: jobsApi.updateAgreement,
    setArchived: jobsApi.setAgreementArchived,
    remove: jobsApi.removeAgreement,
    url: jobsApi.agreementUrl,
  },
};
